import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';

import {FormInput} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark/required-mark';
import {MessageContext} from 'src/contexts/message-context';
import {PricingModel} from 'src/openapi/admin/models/PricingModel';

import Button from '@pb/kit-component-button';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group, Item} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {RadioButton, RadioButtons} from '@pb/kit-component-radio-button';
import {Textarea} from '@pb/kit-component-textarea-v2';

import {PRICING_MODEL_LABEL} from '../../../constants';
import {crmIntegrationSelector} from '../../setup/services/selectors';
import {INPUT_WIDTH} from '../constants';
import {useCreateContract} from '../hooks';

import {IntegrationContractDetails} from './integration-contract-details';
import {PPCContractDetails} from './ppc-contract-details';
import {SubscriptionContractDetails} from './subscription-contract-details';

import {apiV2AddContract} from './add-contract-modal.module.styl';

import type {OnCloseModal} from './typedef';


type Props = {
    onClose: OnCloseModal,
    onCreateContract: (n: string) => void,
};

export const AddContractModal = (props: Props) => {
    const crmIntegration = useSelector(crmIntegrationSelector);
    const {setErrorMessage} = useContext(MessageContext);

    const {
        busy: disabled,
        error,
        onEditIntegrationData,
        onEditSubscriptionData,
        onEditPPCData,
        saveAvailable,
        onCreateContract,
    } = useCreateContract(props.onCreateContract);

    const [comment, setComment] = useState('');
    const [contractNumber, setContractNumber] = useState('');
    const [pricingModel, setPricingModel] = useState<PricingModel | null>(null);
    const [hasInvalidDateRange, setHasInvalidDateRange] = useState(false);

    const isSubscription = pricingModel === PricingModel.SUBSCRIPTION;
    const isPayPerCall = pricingModel === PricingModel.PAY_PER_CALL;
    const isIntegration = pricingModel === PricingModel.INTEGRATION;
    const modalWidth = isSubscription || isIntegration ? 600 : 500;
    const canSave = contractNumber && pricingModel && saveAvailable && !hasInvalidDateRange;

    useEffect(() => {
        if (error) {
            setErrorMessage(error.message ?? '');
        }
    }, [error, setErrorMessage]);

    const radioButtons = crmIntegration
        ? [PricingModel.INTEGRATION]
        : [PricingModel.SUBSCRIPTION, PricingModel.PAY_PER_CALL];

    const onSave = useCallback(() => {
        if (pricingModel) {
            onCreateContract(pricingModel, {contractNumber, comment});
        }
    }, [pricingModel, onCreateContract, contractNumber, comment]);


    return (
        <>
            <DialogHeader onClose={props.onClose}>
                Contract Details
            </DialogHeader>
            <DialogMain style={{padding: 24, maxWidth: modalWidth}} className={apiV2AddContract}>
                <Group gap="xl" direction="v">
                    <Group gap="l" alignItems="end">
                        <Item>
                            <Label><RequiredMark label="Contract #"/></Label>
                            <FormInput
                                value={contractNumber}
                                width={INPUT_WIDTH}
                                maxLength={255}
                                onBlur={setContractNumber}
                            />
                        </Item>
                        <Item className={crmIntegration ? 'pb-s' : undefined}>
                            <RadioButtons
                                {...{
                                    disabled,
                                    name: 'pricingModel',
                                    value: pricingModel,
                                    onChange: setPricingModel,
                                }}>
                                {radioButtons.map(value => (
                                    <RadioButton
                                        key={value}
                                        {...{
                                            label: PRICING_MODEL_LABEL[value],
                                            showLabel: true,
                                            value,
                                        }}
                                    />
                                ))}
                            </RadioButtons>
                        </Item>
                    </Group>
                    {isIntegration
                        ? (
                                <IntegrationContractDetails
                                    {...{
                                        disabled,
                                        onEditData: onEditIntegrationData,
                                        onHasInvalidRange: setHasInvalidDateRange,
                                    }}
                                />
                            )
                        : null}
                    {isSubscription
                        ? (
                                <SubscriptionContractDetails
                                    onEditData={onEditSubscriptionData}
                                    onHasInvalidRange={setHasInvalidDateRange}
                                />
                            )
                        : null}
                    {isPayPerCall ? (<PPCContractDetails{...{disabled, onEditData: onEditPPCData}}/>) : null}
                    {pricingModel ? (
                        <Item>
                            <Label>Comment</Label>
                            <Textarea
                                {...{
                                    disabled,
                                    value: comment,
                                    resizable: false,
                                    height: 80,
                                    maxLength: 255,
                                    onChange: e => setComment(e.target.value),
                                }}
                            />
                        </Item>
                    ) : null}
                </Group>
            </DialogMain>
            <DialogFooter>
                <Group alignItems="center">
                    <Button variant="Type.PRIMARY" disabled={!canSave} busy={disabled} onClick={onSave}>Save</Button>
                    <Button variant="Type.SECONDARY" busy={disabled} onClick={props.onClose}>Cancel</Button>
                </Group>
            </DialogFooter>
        </>
    );
};
